import { z } from 'zod';
import { isoUtcDateTimeSchema, positiveIntIdSchema } from '../shared/schemas.js';

export const actionIdSchema = positiveIntIdSchema;

export const actionCategoryIdSchema = positiveIntIdSchema;

/** Ensemble de tags d'une action : doublons retirés, ids triés (TD-026). */
export const actionTagIdsSchema = z
  .array(positiveIntIdSchema)
  .max(20, 'Une action ne peut pas porter plus de 20 tags.')
  .transform((ids) => [...new Set(ids)].sort((a, b) => a - b));

export const actionDescriptionSchema = z
  .string()
  .trim()
  .min(1, 'La description est obligatoire.')
  .max(500, 'La description ne peut pas dépasser 500 caractères.');

export const actionSchema = z.object({
  id: z.number().int().positive(),
  description: z.string(),
  categoryId: z.number().int().positive(),
  tagIds: z.array(z.number().int().positive()),
  archived: z.boolean(),
  archivedAt: isoUtcDateTimeSchema.nullable(),
  createdAt: isoUtcDateTimeSchema,
  updatedAt: isoUtcDateTimeSchema,
});

export type Action = z.infer<typeof actionSchema>;

export const createActionSchema = z.object({
  description: actionDescriptionSchema,
  categoryId: actionCategoryIdSchema,
  tagIds: actionTagIdsSchema.default([]),
});

export type CreateActionInput = z.infer<typeof createActionSchema>;

export const updateActionSchema = z
  .object({
    description: actionDescriptionSchema.optional(),
    categoryId: actionCategoryIdSchema.optional(),
    /** Remplace l'ensemble des tags de l'action. */
    tagIds: actionTagIdsSchema.optional(),
  })
  .strict()
  .refine((input) => input.description !== undefined || input.categoryId !== undefined || input.tagIds !== undefined, {
    message: 'Indiquez au moins un champ à modifier.',
  });

export type UpdateActionInput = z.infer<typeof updateActionSchema>;

export const actionListSchema = z.object({
  actions: z.array(actionSchema),
});

export type ActionList = z.infer<typeof actionListSchema>;

export const ACTION_PAGE_DEFAULT_LIMIT = 50;
export const ACTION_PAGE_MAX_LIMIT = 100;

/** Liste d'ids en paramètre de requête : « 3,7 ». */
const idListQuerySchema = z
  .string()
  .regex(/^\d+(,\d+)*$/, "Liste d'identifiants attendue, par exemple « 3,7 ».")
  .transform((value) => [...new Set(value.split(',').map(Number))])
  .pipe(z.array(positiveIntIdSchema).max(20));

/**
 * Recherche dans le catalogue (TD-026), en paramètres de requête. Seules les
 * actions du catalogue sont concernées, jamais les actions personnalisées.
 */
export const actionSearchQuerySchema = z
  .object({
    /** Mots que la description doit tous contenir, sans tenir compte de la casse ni des accents. */
    search: z.string().trim().max(100).optional(),
    categoryId: z.coerce.number().pipe(actionCategoryIdSchema).optional(),
    /** L'action doit porter tous ces tags. */
    tagIds: idListQuerySchema.optional(),
    /** L'action ne doit porter aucun de ces tags. */
    excludeTagIds: idListQuerySchema.optional(),
    includeArchived: z
      .enum(['true', 'false'])
      .optional()
      .transform((value) => value === 'true'),
    limit: z.coerce.number().int().min(1).max(ACTION_PAGE_MAX_LIMIT).default(ACTION_PAGE_DEFAULT_LIMIT),
    offset: z.coerce.number().int().min(0).default(0),
  })
  .strict();

export type ActionSearchQuery = z.infer<typeof actionSearchQuerySchema>;

/**
 * Filtre du catalogue en corps JSON, pour les commandes en masse sur les
 * actions d'une opération (TD-026). Un filtre vide vise tout le catalogue.
 */
export const actionFilterSchema = z
  .object({
    search: z.string().trim().max(100).optional(),
    categoryId: actionCategoryIdSchema.optional(),
    tagIds: actionTagIdsSchema.optional(),
    excludeTagIds: actionTagIdsSchema.optional(),
  })
  .strict();

export type ActionFilter = z.infer<typeof actionFilterSchema>;

export const actionPageSchema = z.object({
  actions: z.array(actionSchema),
  /** Nombre total d'actions qui répondent aux critères, toutes pages confondues. */
  total: z.number().int().nonnegative(),
  limit: z.number().int().positive(),
  offset: z.number().int().nonnegative(),
});

export type ActionPage = z.infer<typeof actionPageSchema>;
